import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import ReactModal from "react-modal";
import styled from "styled-components";

import Button from "../../ui/Button/Button";

type ContextType = {
  isVisible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  title: string | ReactElement;
  setTitle: Dispatch<SetStateAction<string | ReactElement>>;
  message: string | ReactElement;
  setMessage: Dispatch<SetStateAction<string | ReactElement>>;
};

const AlertContext = createContext({} as ContextType);

interface Props {
  children: ReactNode;
}

const Context: FC<Props> = ({ children }) => {
  const [isVisible, setVisible] = useState<boolean>(false);
  const [title, setTitle] = useState<string | ReactElement>("");
  const [message, setMessage] = useState<string | ReactElement>("");

  const value = useMemo(() => {
    return {
      isVisible,
      setVisible,
      title,
      setTitle,
      message,
      setMessage,
    };
  }, [isVisible, message, title]);

  return (
    <AlertContext.Provider value={value}>
      {children}
      <ReactModal
        ariaHideApp={false}
        isOpen={isVisible}
        onRequestClose={() => setVisible(false)}
        style={{
          content: {
            minWidth: 300,
            maxWidth: 600,
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
          },
          overlay: {
            zIndex: 9999,
          },
        }}
      >
        {title && <StyledTitle>{title}</StyledTitle>}
        <StyledText>{message}</StyledText>
        <StyledBtns>
          <Button onClick={() => setVisible(false)}>OK</Button>
        </StyledBtns>
      </ReactModal>
    </AlertContext.Provider>
  );
};

export const useAlertContext = () => {
  const { setVisible, setTitle, setMessage } = useContext(AlertContext);

  const showMsg = useCallback(
    (message: unknown, title?: string) => {
      setVisible(true);
      setTitle(title || "");
      setMessage(typeof message === "object" ? JSON.stringify(message) : String(message));
    },
    [setVisible, setTitle, setMessage]
  );

  const showMsgBlock = useCallback(
    (content: ReactElement) => {
      setVisible(true);
      setMessage(content);
    },
    [setVisible, setMessage]
  );

  const hideMsg = useCallback(() => {
    setVisible(false);
    setTitle(undefined);
    setMessage(undefined);
  }, [setVisible, setTitle, setMessage]);

  return { showMsg, showMsgBlock, hideMsg };
};

export default Context;

const StyledTitle = styled("div")`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: var(--color-navy);
`;

const StyledText = styled("div")`
  display: flex;
  justify-content: center;
  text-align: center;
  font-size: 15px;
  font-weight: 400;
  line-height: 23px;
  color: black;
`;

const StyledBtns = styled("div")`
  display: flex;
  justify-content: center;
  margin: 20px 0 0 0;
`;
